import { Icon } from '@material-ui/core';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import Grid from '@material-ui/core/Grid';
import { withStyles, withTheme } from '@material-ui/core/styles';
import React from 'react';
import styled, { css } from 'styled-components';
import ArrowDown from 'utils/images/arrow-down.svg';
import PriceWithSymbol from '../../../../../../../utils/currency/PriceWithSymbol';
import MenuTag from '../../shared/MenuTag';
import MenuTooltip from '../../shared/MenuTooltip';
import EditButton from '../EditButton';
import PublishStatus from '../PublishStatus';

const PanelWithStyles = withStyles({
  root: {
    width: '100%',
    display: 'block',
    padding: '0 !important',
    boxShadow: 'none',
    textAlign: 'left !important',
    borderBottom: 'none',
    '&:before': {
      display: 'none',
    },
  },
  expanded: {
    margin: '0',
  },
})(ExpansionPanel);

const Pane = withStyles({
  root: {
    width: '100%',
    display: 'block',
    padding: '0 !important',
    margin: '0',
    boxShadow: 'none',
    borderBottom: '1px solid #EEEEEE',
  },
})(ExpansionPanelDetails);

const PaneSummary = withStyles({
  root: {
    padding: '0 0 0 20px !important',
    margin: '0 !important',
    boxShadow: 'none',
    borderBottom: '1px solid #EEEEEE',
    minHeight: '0 !important',
    cursor: 'default',
    '&:hover': {
      cursor: 'default !important',
    },
  },
  content: {
    margin: '0px !important',
    '&> :last-child': {
      paddingRight: '20px',
    },
  },
})(ExpansionPanelSummary);

const DragIndicator = styled(Icon)`
  color: #c4c4c4;
  display: flex;
  align-items: center;
  margin-left: -13px;
  margin-right: 0px;
  margin-top: 4px;

  &:hover {
    cursor: grab;
    ${props =>
      props.isAnyChildItemExpanded === false
        ? css`
            cursor: grab;
          `
        : css`
            cursor: pointer;
          `}
    ${props =>
      props.isAnyChildItemExpanded === false
        ? css`
            color: black;
          `
        : css`
            color: transparent;
          `}
  }

  &:active {
    color: black;
  }

  ${props =>
    props.isAnyChildItemExpanded === false
      ? css`
          margin-top: 4px;
        `
      : css`
          color: transparent;
        `}
  ${props =>
    props.level === 0
      ? css`
          margin-top: 4px;
        `
      : css`
          margin-top: 0px;
        `}
  & {
    ${props =>
      props.hideindicator &&
      css`
        color: transparent;
        pointer-events: none;
      `}
  }
`;

const ExpansionHeader = styled.div`
  display: flex;
  width: 100%;

  ${props => css`
    margin: ${props.hideHeaderMargin ? '5px' : '20px'} 0;
  `}

  h1 {
    margin-block-end: 0;
  }

  h2 {
    margin-block-start: 0;
  }
`;

const HeaderTextFontSize = {
  0: '25px',
  1: '18px',
  2: '16px',
};

const HeaderText = styled.h1`
  padding-left: 7px;
  min-height: 24px;
  vertical-align: middle;
  display: flex;
  align-items: center;

  ${props => css`
    letter-spacing: ${props.level < 3 ? '-0.4px' : '-0.2px'};
    font-family: ${props.level < 1 ? 'Geomanist' : 'Basier Circle'};
    font-size: ${HeaderTextFontSize[props.level] || '13px'} !important;
    font-weight: ${props.expanded ? '600' : '400'};
  `}

  ${props =>
    props.level === 0 &&
    css`
      font-weight: 500;
    `}
`;

const ExpansionHeaderRight = styled.div`
  width: 25%;
  text-align: right;
  font-size: 14px;
`;

const ExpansionHeaderLeft = styled.div`
  width: 75%;
  text-align: left !important;
  cursor: pointer;

  ${props =>
    props.level &&
    css`
      padding-left: ${props.level * (props.level > 2 ? 35 : 40)}px;
    `}

  @media (max-width: 900px) {
    ${props =>
      props.level &&
      css`
        padding-left: ${10 * props.level}px;
      `}
  }

  ${props =>
    props.hideExpansion &&
    css`
      cursor: default;
    `}
`;

const ControlContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  height: 30px;
`;

const ExpanderArrow = styled.div`
  float: left;
  margin: 0 8px;
  font-size: 12px;
  line-height: 12px;
  transform: rotate(270deg);

  ${props => css`
    margin-top: ${props.level === 0 ? '10px' : '6px'};
  `}

  ${props =>
    props.expanded &&
    css`
      transform: rotate(0deg);
    `}

  ${props =>
    props.hide &&
    css`
      visibility: hidden;
    `}
`;

const HeaderContent = styled.div`
  padding-left: 42px;
  justify-content: space-between;
  font-size: 13px;
`;

const Label = styled.span`
  font-family: Geomanist;
  font-size: 20px;
  font-weight: 500;
  line-height: 20px;
  color: #fff;
  background-color: #afafaf;
  padding: 2px 9px;
  margin-left: 10px;
  border-radius: 28px;
  vertical-align: unset;
  margin-bottom: 1px;

  ${props =>
    props.live &&
    css`
      background-color: #000;
    `}
`;

const Name = styled.span`
  font-family: inherit;
  vertical-align: unset;
`;

const Price = styled.span`
  font-size: 13px;
  padding: 5px 0;
`;

const ChoiceLimit = styled.span`
  font-size: 13px;
  padding: 5px 0;

  ${props =>
    props.expanded &&
    css`
      font-weight: 600;
    `}
`;

const HeaderRow = styled.div``;

const Description = styled.p`
  margin-left: 4px;
  margin-bottom: 0;
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
`;
const PriceWrapper = styled.div`
  position: 'relative';
  left: '220px';
  margin-top: '5px';
`;

function Panel(props) {
  const {
    key,
    item,
    price,
    theme,
    level,
    onEdit,
    children,
    choiceLimit,
    hideBorders,
    hideHeaderMargin,
    hideExpandedSummaryBorder,
    isEditMode,
    showMenuEditorLevel0,
    isExpanded,
    setIsExpanded,
    isAnyChildItemExpanded = false,
    topLevel = false,
    hasOptionGroups = false,
  } = props;

  const expanded = isExpanded[item.id] || false;

  const hasChildren = item.children && item.children.length > 0;
  const hasOgVariants = item.og_variants && item.og_variants.length > 0;

  let hideExpansion =
    level > 1 && !hasChildren && !item.description && !hasOptionGroups;
  let paneStyleOverride = {};
  let paneSummaryStyleOverride = {};
  if (hideBorders) {
    paneStyleOverride = { borderBottom: 'none' };
  }

  if (hideBorders || (hideExpandedSummaryBorder && expanded)) {
    paneSummaryStyleOverride = { borderBottom: 'none' };
  }

  return (
    <PanelWithStyles key={key} expanded={expanded}>
      <PaneSummary expandIcon={null} style={paneSummaryStyleOverride}>
        <ExpansionHeader hideHeaderMargin={hideHeaderMargin}>
          <ExpansionHeaderLeft level={level} hideExpansion={hideExpansion}>
            <div
              onClick={event => {
                if (event.target.innerText === 'drag_indicator') {
                  return;
                }

                if (!hideExpansion) {
                  const newExpansionStatus = {
                    ...isExpanded,
                    [item.id]: !expanded,
                  };
                  setIsExpanded(newExpansionStatus);
                }
              }}
            >
              <HeaderRow>
                <Grid wrap="nowrap" container>
                  <MenuTooltip
                    topLevel={topLevel}
                    isAnyChildItemExpanded={isAnyChildItemExpanded}
                  >
                    <DragIndicator
                      hideindicator={
                        isEditMode || showMenuEditorLevel0 || expanded
                      }
                      level={level}
                      isAnyChildItemExpanded={isAnyChildItemExpanded}
                    >
                      drag_indicator
                    </DragIndicator>
                  </MenuTooltip>
                  <Grid item xs={8} style={{ marginRight: '-11px' }}>
                    <ExpanderArrow
                      level={level}
                      expanded={expanded}
                      hide={hideExpansion}
                    >
                      <img alt="expand arrow" src={ArrowDown} />
                    </ExpanderArrow>
                    <ItemRow>
                      <HeaderText
                        level={level}
                        expanded={expanded && !hideExpansion}
                      >
                        <Name>{item.name}</Name>{' '}
                        {item.clientOnly && <MenuTag>Clients</MenuTag>}{' '}
                        {hasChildren && (
                          <Label live={item.status === 'live'}>
                            {item.children.length}
                          </Label>
                        )}
                        {hasOgVariants && !hasChildren && (
                          <Label live={item.status === 'live'}>
                            {item.og_variants.length}
                          </Label>
                        )}
                      </HeaderText>
                      <PriceWrapper>
                        {price && (
                          <Price>
                            <PriceWithSymbol price={price} />
                          </Price>
                        )}
                      </PriceWrapper>
                    </ItemRow>
                  </Grid>
                  <Grid item container justify="flex-end" xs={4}>
                    {choiceLimit ? (
                      <ChoiceLimit expanded={expanded}>
                        {choiceLimit}
                      </ChoiceLimit>
                    ) : null}
                  </Grid>
                </Grid>
              </HeaderRow>

              <HeaderContent>
                <Grid container spacing={8}>
                  <Grid item xs={8}>
                    {expanded && item.description ? (
                      <Description>{item.description}</Description>
                    ) : null}
                  </Grid>
                </Grid>
              </HeaderContent>
            </div>
          </ExpansionHeaderLeft>
          <ExpansionHeaderRight>
            <ControlContainer>
              {item.status && item.type !== 'variant' ? (
                <PublishStatus
                  level={level}
                  theme={theme}
                  status={item.status}
                  label={item.label}
                />
              ) : null}

              {item.type !== 'variant' ? (
                <EditButton
                  onClick={() => {
                    // Ensure draw is expanded when editor is shown
                    const newExpansionStatus = {
                      ...isExpanded,
                      [item.id]: true,
                    };
                    setIsExpanded(newExpansionStatus);
                    onEdit();
                  }}
                />
              ) : null}
            </ControlContainer>
          </ExpansionHeaderRight>
        </ExpansionHeader>
      </PaneSummary>
      {expanded && <Pane style={paneStyleOverride}>{children}</Pane>}
    </PanelWithStyles>
  );
}

export default withTheme()(Panel);



// WEBPACK FOOTER //
// ./src/components/Dashboard/Panels/MenuEditor/components/shared/Panel/panel.jsx